import { createFileRoute } from "@tanstack/react-router";
import { PageHero } from "@/components/PageHero";
import { img } from "@/lib/images";
import { site } from "@/lib/site";
import { canonicalLinks, routeMeta } from "@/lib/seo";

export const Route = createFileRoute("/privacy")({
  head: () => ({
    meta: [
      ...routeMeta("/privacy"),
      { title: "Privacy Policy — CCOB And Company PA" },
      { name: "description", content: "How CCOB And Company PA collects, uses, and protects your personal and financial information." },
      { property: "og:title", content: "Privacy Policy — CCOB And Company PA" },
      { property: "og:description", content: "Our commitment to protecting your information." },
    ],
    links: canonicalLinks("/privacy"),
  }),
  component: PrivacyPage,
});

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mt-10">
      <h2 className="font-display text-2xl text-primary">{title}</h2>
      <div className="gold-line mt-3" />
      <div className="mt-4 text-foreground/80 leading-relaxed space-y-3 text-[15px]">{children}</div>
    </div>
  );
}

function PrivacyPage() {
  return (
    <>
      <PageHero eyebrow="Legal" title="Privacy Policy" subtitle="Your trust is the foundation of our work. Here's how we protect your information." image={img.heroTerms} />
      <section className="py-20">
        <div className="container-x max-w-3xl">
          <p className="text-sm text-muted-foreground uppercase tracking-widest">Last updated: May 2026</p>

          <Section title="Our Commitment">
            <p>{site.name} ("we," "us," or "our") is committed to protecting the privacy and confidentiality of every individual and business we serve. As a CPA firm, we're bound by professional standards of confidentiality in addition to the practices described below.</p>
          </Section>

          <Section title="Information We Collect">
            <p>We collect information you provide directly — for example when you request a consultation, complete a tax organizer, send us documents, or contact us through this website. This may include your name, address, phone number, email, Social Security Number, financial records, and tax documents.</p>
            <p>Our website may also automatically collect basic technical information (IP address, browser type, pages visited) using standard analytics tools to help us improve the site.</p>
          </Section>

          <Section title="How We Use Your Information">
            <p>We use the information you provide to deliver the services you've engaged us for — preparing tax returns, maintaining books, processing payroll, providing advisory services, and communicating with you about your account. We do not sell your information. Ever.</p>
          </Section>

          <Section title="Information Sharing">
            <p>We share your information only when necessary to deliver our services or when legally required: with the IRS or state tax authorities when filing returns on your behalf, with service providers under confidentiality agreements (such as secure document portals or payroll processors), or in response to a valid subpoena or court order.</p>
          </Section>

          <Section title="How We Protect Your Information">
            <p>We use physical, electronic, and procedural safeguards to protect your information, including encrypted client portals, secure document storage, restricted staff access, and routine security reviews. No system is 100% secure, but we treat your data with the same care we'd want for our own.</p>
          </Section>

          <Section title="Cookies & Analytics">
            <p>This website uses cookies and similar technologies to remember preferences and measure traffic. You can disable cookies in your browser; some site features may not work as expected if you do.</p>
          </Section>

          <Section title="Your Choices">
            <p>You may request access to, correction of, or deletion of personal information we hold about you. You may also opt out of marketing communications at any time. Note that we are required to retain certain client records under IRS and state regulations.</p>
          </Section>

          <Section title="Contact Us">
            <p>Questions about this policy? Reach out:</p>
            <p>{site.name}<br />{site.address1}<br />{site.address2}<br />Phone: {site.phone}<br /><a href="/contact#consultation" className="text-gold hover:underline">Send a secure message</a></p>
          </Section>
        </div>
      </section>
    </>
  );
}
